import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const AdminTickerPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Fetch ticker messages
  const fetchMessages = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await fetch(`${backendUrl}/api/admin/ticker`, {
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Error fetching ticker messages:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  // Add new message
  const handleAdd = async () => {
    if (!newMessage.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await fetch(`${backendUrl}/api/admin/ticker`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${auth}`,
        },
        body: JSON.stringify({ text: newMessage.toUpperCase() }),
      });

      if (response.ok) {
        toast.success('Message ajouté avec succès');
        setNewMessage('');
        setShowAddForm(false);
        fetchMessages();
      } else {
        toast.error('Erreur lors de l\'ajout');
      }
    } catch (error) {
      console.error('Error adding message:', error);
      toast.error('Erreur lors de l\'ajout');
    }
  };

  // Update message
  const handleUpdate = async (id) => {
    if (!editText.trim()) {
      toast.error('Le message ne peut pas être vide');
      return;
    }

    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await fetch(`${backendUrl}/api/admin/ticker/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${auth}`,
        },
        body: JSON.stringify({ text: editText.toUpperCase() }),
      });

      if (response.ok) {
        toast.success('Message mis à jour');
        setEditingId(null);
        setEditText('');
        fetchMessages();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating message:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete message
  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      return;
    }

    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await fetch(`${backendUrl}/api/admin/ticker/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Basic ${auth}`,
        },
      });

      if (response.ok) {
        toast.success('Message supprimé');
        fetchMessages();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C9A84C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-ticker-page">
      {/* Header */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-white">Messages du Ruban</h1>
          <p className="text-gray-400 mt-1">
            Gérez les messages qui défilent sur la page d'accueil
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 px-4 py-2 bg-[#C9A84C] text-black rounded-lg hover:bg-[#B39840] transition-colors whitespace-nowrap flex-shrink-0"
          data-testid="add-ticker-btn"
        >
          <Plus className="w-5 h-5" />
          <span className="hidden sm:inline">Ajouter un message</span>
          <span className="sm:hidden">Ajouter</span>
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Nouveau message</h3>
          <div className="flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="VOTRE MESSAGE EN MAJUSCULES"
              className="flex-1 px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A84C]"
              data-testid="ticker-input"
            />
            <button
              onClick={handleAdd}
              className="px-6 py-2 bg-[#C9A84C] text-black rounded-lg hover:bg-[#B39840] transition-colors"
              data-testid="save-ticker-btn"
            >
              Ajouter
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewMessage('');
              }}
              className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Annuler
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            💡 Les messages seront automatiquement convertis en majuscules
          </p>
        </div>
      )}

      {/* Messages List */}
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-2 text-gray-400">
            <MessageSquare className="w-5 h-5" />
            <span className="font-medium">{messages.length} message(s) dans le ruban</span>
          </div>
        </div>

        {messages.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun message dans le ruban</p>
            <p className="text-sm mt-2">Cliquez sur "Ajouter un message" pour commencer</p>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {messages.map((message) => (
              <div
                key={message.id}
                className="p-4 hover:bg-white/5 transition-colors"
                data-testid={`ticker-item-${message.id}`}
              >
                {editingId === message.id ? (
                  <div className="flex gap-3 items-center">
                    <input
                      type="text"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="flex-1 px-4 py-2 bg-black/40 border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#C9A84C]"
                      data-testid="edit-ticker-input"
                    />
                    <button
                      onClick={() => handleUpdate(message.id)}
                      className="p-2 bg-[#C9A84C] text-black rounded-lg hover:bg-[#B39840] transition-colors"
                      data-testid="save-edit-btn"
                    >
                      <Save className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setEditText('');
                      }}
                      className="p-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-between items-center gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="text-[#C9A84C] text-lg flex-shrink-0">◆</span>
                      <span className="text-white font-mono text-sm md:text-base tracking-wide truncate">
                        {message.text}
                      </span>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => {
                          setEditingId(message.id);
                          setEditText(message.text);
                        }}
                        className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors"
                        data-testid={`edit-ticker-${message.id}`}
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(message.id)}
                        className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-500 transition-colors"
                        data-testid={`delete-ticker-${message.id}`}
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview */}
      {messages.length > 0 && (
        <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Aperçu du ruban</h3>
          <div className="w-full border border-white/10 bg-black/60 py-3 overflow-hidden rounded-lg">
            <div className="animate-marquee whitespace-nowrap flex">
              {[...messages, ...messages].map((message, index) => (
                <span key={index} className="inline-flex items-center mx-6">
                  <span className="text-xs tracking-[0.22em] uppercase text-[#B9B2A8]">
                    {message.text}
                  </span>
                  <span className="ml-6 text-[#C9A84C]">◆</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTickerPage;